"use client";
import React, { createContext, useContext, useState, useCallback } from "react";
import { BASE_URL } from "../config/config";

const SupportCategoryContext = createContext();

export const SupportCategoryProvider = ({ children }) => {
  const [supportCategories, setSupportCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const token = localStorage.getItem("token");

  // 🔹 FETCH SUPPORT CATEGORIES
  const fetchSupportCategories = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");

    try {
      const res = await fetch(`${BASE_URL}/support-category`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (res.ok && data.status === 200) {
        setSupportCategories(data.data || []);
      } else {
        setError(data.message || "Failed to fetch support categories");
      }
    } catch (err) {
      setError("Network error");
      console.error("Fetch support categories error:", err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔹 ADD SUPPORT CATEGORY
  const addSupportCategory = async (categoryData) => {
    if (!token) return false;
    setLoading(true);
    setError("");

    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");

      const res = await fetch(`${BASE_URL}/support-category`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: categoryData.name,
          create_audit_id: user.id,
        }),
      });

      const data = await res.json();

      if (res.ok && (data.status === 200 || data.status === 201)) {
        await fetchSupportCategories();
        return true;
      } else {
        setError(data.message || "Failed to create support category");
        return false;
      }
    } catch (err) {
      setError("Network error");
      console.error("Add support category error:", err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // 🔹 UPDATE SUPPORT CATEGORY
  const updateSupportCategory = async (id, categoryData) => {
    if (!token) return false;
    setLoading(true);
    setError("");

    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");

      const res = await fetch(`${BASE_URL}/support-category/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: categoryData.name,
          status: categoryData.status,
          update_audit_id: user.id,
        }),
      });

      const data = await res.json();

      if (res.ok && data.status === 200) {
        await fetchSupportCategories();
        return true;
      } else {
        setError(data.message || "Failed to update support category");
        return false;
      }
    } catch (err) {
      setError("Network error");
      console.error("Update support category error:", err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // 🔹 DELETE SUPPORT CATEGORY
  const deleteSupportCategory = async (id) => {
    if (!token) return false;
    if (!window.confirm("Delete this support category?")) return false;

    setLoading(true);
    setError("");

    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");

      const res = await fetch(`${BASE_URL}/support-category/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          delete_audit_id: user.id,
        }),
      });

      const data = await res.json();

      if (res.ok && data.status === 200) {
        setSupportCategories(prev => prev.filter(cat => cat.id !== id));
        return true;
      } else {
        setError(data.message || "Failed to delete support category");
        return false;
      }
    } catch (err) {
      setError("Network error");
      console.error("Delete support category error:", err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // 🔹 TOGGLE CATEGORY STATUS
  const toggleCategoryStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === "Active" ? "Inactive" : "Active";

    try {
      const category = supportCategories.find(cat => cat.id === id);
      if (category) {
        await updateSupportCategory(id, {
          name: category.name,
          status: newStatus
        });
      }
    } catch (err) {
      console.error("Toggle status error:", err);
    }
  };

  return (
    <SupportCategoryContext.Provider
      value={{
        supportCategories,
        loading,
        error,
        fetchSupportCategories,
        addSupportCategory,
        updateSupportCategory,
        deleteSupportCategory,
        toggleCategoryStatus,
      }}
    >
      {children}
    </SupportCategoryContext.Provider>
  );
};

export const useSupportCategories = () => {
  const context = useContext(SupportCategoryContext);
  if (!context) {
    throw new Error("useSupportCategories must be used within a SupportCategoryProvider");
  }
  return context;
};
